import { useState, useRef, useEffect, FC } from "react";
import { Card, Button, Typography, Tag, Spin, Badge } from "antd";
import {
  PlusOutlined,
  UpOutlined,
  DownOutlined,
  EyeOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import Title from "antd/es/typography/Title";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import { useDispatch, useSelector } from "react-redux";
import { QueryData } from "../../../../interfaces/IPreAuthQuery";
import { AppDispatch, RootState } from "../../../../store/store";
import { fetchQueries } from "../../../../models/preAuthQuerySlice";
import { queries } from "@testing-library/react";
import { getPreAuthStepper } from "../../../../apis/patientService";

dayjs.extend(timezone);
const { Text } = Typography;

type PatientDetailsQueryCardAllStagesProps = {
  activeKey?: any;
  handleCreateQuery: (stageId: string) => void;
  visitStages: any[];
  handleViewQueryModal: (query: QueryData) => void;
  handleReplyQueryModal: (query: QueryData) => void;
  setQueryStageId?: any;
  setActiveKey?: any;
};

const PatientDetailsQueryCardAllStages: FC<
  PatientDetailsQueryCardAllStagesProps
> = ({
  activeKey,
  handleCreateQuery,
  visitStages,
  handleViewQueryModal,
  handleReplyQueryModal,
  setQueryStageId,
  setActiveKey,
}) => {
  const { auth, preAuth, patients, queries } = useSelector(
    (state: RootState) => state,
  );
  const dispatch = useDispatch<AppDispatch>();
  const [cardCollapsed, setCardCollapsed] = useState(false);
  //   const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const [stageQueries, setStageQueries] = useState<Record<string, any>>({});
  // const [loadingStages, setLoadingStages] = useState<Record<string, boolean>>(
  //   {},
  // );
  const [loadingStages, setLoadingStages] = useState<boolean>(false);
  const [expandedStageId, setExpandedStageId] = useState<string | null>(null);
  const [stageWisePendingQueryCount, setStageWisePendingQueryCount] = useState<
    Record<string, number>
  >({});
  const [totalPendingQueriesCount, setTotalPendingQueriesCount] =
    useState<number>(0);

  const fetchStageQueries = async (stageId: any) => {
    // setLoadingStages((prev) => ({ ...prev, [stageId]: true }));
    setLoadingStages(true);
    try {
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const portalMetaDataId =
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0];
      if (caseId && visitId && portalMetaDataId) {
        const res: any = await dispatch(
          fetchQueries({
            caseId,
            visitId,
            portalMetaDataId,
            stageId: stageId, //parseInt(stageId)
          }),
        );

        // let pendingCount = 0;
        // if (res?.payload?.length > 0) {
        //   res?.payload?.forEach(
        //     (query: any) => query?.stateName === "Query" && pendingCount++,
        //   );
        // }

        // setStageQueries((prev) => ({ ...prev, [stageId]: res.payload || [] }));
        // setStageWisePendingQueryCount((prev) => ({
        //   ...prev,
        //   [stageId]: pendingCount,
        // }));
        // setTotalPendingQueriesCount((prev) => prev + pendingCount);

        const responseMap = res?.payload || {};

        let totalPending = 0;

        const stageWiseQueries: Record<string, any[]> = {};
        const stageWisePending: Record<string, number> = {};

        Object.entries(responseMap)?.forEach(([stageId, queries]: any) => {
          const pendingCount = (queries || [])?.filter(
            (q: any) => q?.stateName === "Query",
          ).length;

          stageWiseQueries[stageId] = queries || [];
          stageWisePending[stageId] = pendingCount;

          totalPending += pendingCount;
        });

        setStageQueries(stageWiseQueries);
        setStageWisePendingQueryCount(stageWisePending);
        setTotalPendingQueriesCount(totalPending);
      }
    } catch (err) {
      console.error("Failed to fetch queries for stage", stageId, err);
    } finally {
      // setLoadingStages((prev) => ({ ...prev, [stageId]: false }));
      setLoadingStages(false);
    }
  };

  //   const toggleStage = (stageId: string) => {
  //     setExpandedStages((prev) => {
  //       const newSet = new Set(prev);
  //       if (newSet.has(stageId)) {
  //         newSet.delete(stageId);
  //       } else {
  //         newSet.add(stageId);
  //         fetchStageQueries(stageId);
  //         // if (!stageQueries[stageId]) {
  //         //   fetchStageQueries(stageId);
  //         // }
  //       }
  //       return newSet;
  //     });
  //   };

  const toggleStage = (stageId: string) => {
    setQueryStageId?.(stageId);
    // setActiveKey(stageId);
    // Keep query stage expand/collapse independent from patient stage collapse (activeKey)
    if (expandedStageId === stageId) {
      setExpandedStageId(null);
    } else {
      setExpandedStageId(stageId);
      fetchStageQueries([stageId]);
    }
  };

  const fetchPreAuthStepper = async () => {
    try {
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      if (caseId && visitId) {
        const response = await getPreAuthStepper(caseId, visitId);
        response?.data?.visitStages?.forEach((stage: any) => {
          if (
            stage?.visitStage !== "Enhancement" &&
            stage?.visitStage !== "Resubmission"
          ) {
            fetchStageQueries(stage?.visitStageId?.toString());
          }
        });
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (visitStages?.length > 0) {
      const visitStagesIds = visitStages?.map((stage) =>
        stage?.visitStageId?.toString(),
      );

      if (visitStagesIds?.length > 0) {
        fetchStageQueries(visitStagesIds);
      }
      // visitStages?.forEach((stage: any) => {
      //   fetchStageQueries(stage?.visitStageId?.toString());
      // });
    }
  }, [visitStages]);

  useEffect(() => {
    const total = Object.values(stageWisePendingQueryCount)?.reduce(
      (sum: number, count: number) => sum + count,
      0,
    );

    setTotalPendingQueriesCount(total);
  }, [stageWisePendingQueryCount]);

  // useEffect(() => {
  //   // if(!expandedStageId) {
  //   //     setExpandedStageId(activeKey);
  //   // }
  //   setExpandedStageId(
  //     typeof activeKey === "string" ? activeKey : activeKey?.[0],
  //   );
  // }, [activeKey]);

  // useEffect(() => {
  //   if (queries?.data?.length > 0) {
  //     setExpandedStageId(queries?.data?.[0]?.stageId?.toString());
  //   }
  // }, [queries?.data]);

  return (
    <Card
      bordered
      style={{
        borderRadius: 12,
        padding: 0,
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div
        style={{
          backgroundColor: "#004C8F",
          padding: "10px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text strong style={{ color: "#fff" }}>
            QUERY
          </Text>
          {/* <Tag
            color="white"
            style={{
              fontSize: 11,
              height: 18,
              lineHeight: "16px",
              color: "rgb(0, 84, 157)",
            }}
          >
            {totalPendingQueriesCount || 0}
          </Tag> */}
          {totalPendingQueriesCount > 0 && (
            <div
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Badge
                count={totalPendingQueriesCount || 0}
                overflowCount={99}
                size="small"
                offset={[4, -2]} // controls badge position
                style={{
                  backgroundColor: "#e02424",
                  fontSize: 10,
                }}
              >
                <Tag
                  color="white"
                  style={{
                    fontSize: 11,
                    height: 18,
                    lineHeight: "16px",
                    color: "rgb(0, 84, 157)",
                    margin: 0,
                  }}
                >
                  New
                </Tag>
              </Badge>
            </div>
          )}
        </div>
        <Button
          size="small"
          icon={cardCollapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={() => setCardCollapsed((prev) => !prev)}
        />
      </div>

      {!cardCollapsed && (
        <div
          style={{
            backgroundColor: "#f8f9fa",
            maxHeight: "75vh",
            overflowY: "auto",
          }}
        >
          {visitStages
            ?.filter(
              (stage) => stage?.visitStage?.toUpperCase() !== "RESUBMISSION",
            )
            ?.filter((stage: any) =>
              patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
                "OP" &&
              patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
                "ECHS"
                ? stage?.visitStage?.toUpperCase() !== "PREAUTH"
                : true,
            )
            ?.map((stage) => {
              const stageId = stage?.visitStageId?.toString();
              const stageLevelId = stage?.stageLevelId?.toString();
              const stageName = stage?.visitStage;
              // const isExpanded = expandedStages.has(stageId?.toString());
              const isExpanded = expandedStageId === stageId?.toString();
              // const isExpanded = true;

              const queriesData = queries?.data || [];
              //  const isEligible =
              //   ((preAuth?.stepperData?.currentVisitStage?.stageLevelId &&
              //   preAuth?.stepperData?.currentVisitStage?.stageLevelId >=
              //     parseInt(stageLevelId) && auth?.role?.role !== "FO_STAFF") || (stage?.stateId && auth?.role?.role !== "FO_STAFF"));

              const currentStageIndex = visitStages?.findIndex(
                (s) => s.stageLevelId === parseInt(stageLevelId),
              );

              const prevStage =
                currentStageIndex > 0
                  ? visitStages[currentStageIndex - 1]
                  : null;

              // const isEligible =
              //   auth?.role?.role !== "FO_STAFF"
              //     ? (preAuth?.stepperData?.currentVisitStage?.stageLevelId &&
              //         preAuth?.stepperData?.currentVisitStage?.stageLevelId >=
              //           parseInt(stageLevelId) &&
              //         stage?.state?.toLowerCase() !== "approved") ||
              //       (stage?.stateId &&
              //         stage?.state?.toLowerCase() !== "approved") ||
              //       (prevStage &&
              //         [
              //           "submitted",
              //           "manually submitted",
              //           "bill uploaded",
              //           "summary generated",
              //         ].includes(prevStage?.state?.toLowerCase()) &&
              //         stage?.state?.toLowerCase() !== "approved")
              //       ? true
              //       : stage?.state?.toLowerCase() === "approved"
              //       ? false
              //       : false
              //     : false;

              const allowedStates = [
                "submitted",
                "manually submitted",
                "bill uploaded",
                "summary generated",
                "approved",
                "bill added",
                "audited",
              ];

              const stageState = stage?.state?.toLowerCase();
              const currentStageLevelId =
                preAuth?.stepperData?.currentVisitStage?.stageLevelId;

              const isEligible =
                auth?.role?.role !== "FO_STAFF" &&
                stageState !== "approved" &&
                ((currentStageLevelId &&
                  currentStageLevelId >= parseInt(stageLevelId)) ||
                  stage?.stateId ||
                  (prevStage &&
                    allowedStates.includes(prevStage?.state?.toLowerCase())));

              const colorCoding = stage?.colorCoding;

              return (
                //   <Card
                //     key={stageId}
                //     style={{ margin: "6px", borderRadius: 8 }}
                //     bodyStyle={{ padding: 12, background: 'rgb(255 249 247)' }}
                //   >
                //     <div
                //       style={{
                //         display: "flex",
                //         justifyContent: "space-between",
                //         alignItems: "center",
                //         cursor: "pointer",
                //         textDecoration: isExpanded ? 'underline solid #008B8B' : "none",
                //         // background: isExpanded ? '#008B8B' : "transparent",
                //         // padding: "8px 4px"
                //       }}
                //       onClick={() => toggleStage(stageId)}
                //     >
                //       <Text strong style={{color: isExpanded ? '#008B8B' : "black", marginLeft: "10px"}}>{stageName}</Text>
                //       <div>
                //         {isEligible && (
                //           <Button
                //             size="small"
                //             icon={<PlusOutlined />}
                //             onClick={(e) => {
                //               e.stopPropagation();
                //               handleCreateQuery(stageId);
                //             }}
                //             style={{ marginRight: 8 }}
                //           />
                //         )}
                //         <Button
                //           size="small"
                //           icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                //         />
                //       </div>
                //     </div>

                //     {isExpanded && (
                //       <div style={{ marginTop: 12 }}>
                //         {loadingStages[stageId] ? (
                //           <Spin />
                //         ) : queriesData?.length ? (
                //           queriesData?.map((query: any) => (
                //             <div
                //               key={query?.id}
                //               style={{
                //                 borderBottom: "0.4px solid #A6A6A6",
                //                 padding: "10px 0",
                //               }}
                //             >
                //               <div
                //                 style={{
                //                   display: "flex",
                //                   alignItems: "center",
                //                   justifyContent: "space-between",
                //                 }}
                //               >
                //                 <div
                //                   style={{
                //                     display: "flex",
                //                     alignItems: "center",
                //                     gap: 8,
                //                   }}
                //                 >
                //                   <Title level={5} style={{ margin: 0, fontSize: "14px" }}>
                //                     {query?.receivedQuery}
                //                   </Title>
                //                   {query?.stateName?.toLowerCase()?.includes("received") && (
                //                     <Tag
                //                       color="red"
                //                       style={{
                //                         fontSize: 10,
                //                         height: 18,
                //                         lineHeight: "16px",
                //                       }}
                //                     >
                //                       new
                //                     </Tag>
                //                   )}
                //                 </div>

                //                 <Tag
                //                   color="#FED7AA"
                //                   style={{
                //                     fontSize: 11,
                //                     height: 18,
                //                     lineHeight: "16px",
                //                     color: "black",
                //                   }}
                //                 >
                //                   {query?.typeOfQuery || "Internal"}
                //                 </Tag>
                //               </div>
                //               <div
                //                 style={{
                //                   display: "flex",
                //                   alignItems: "center",
                //                   justifyContent: "space-between",
                //                 }}
                //               >
                //                 <div>
                //                   <Text
                //                     type="secondary"
                //                     style={{ display: "block", marginTop: 4, fontSize: "11px" }}
                //                   >
                //                     {query?.updatedAt
                //                       ? dayjs(query?.updatedAt)
                //                           .tz("Asia/Kolkata")
                //                           .format("DD MMM YYYY, hh:mm A")
                //                       : null}
                //                   </Text>
                //                   <Tag
                //                     color={
                //                       query?.stateName?.toLowerCase()?.includes("replied")
                //                         ? "#008B8B33"
                //                         : query?.stateName?.toLowerCase()?.includes("received")
                //                         ? "#f0ad4e"
                //                         : "none"
                //                     }
                //                     style={{
                //                       marginTop: 12,
                //                       fontSize: 12,
                //                       borderRadius: 8,
                //                       color:
                //                         query?.stateName?.toLowerCase()?.includes("replied")
                //                           ? "#008B8B"
                //                           : "white",
                //                     }}
                //                   >
                //                     {query?.stateName}
                //                   </Tag>
                //                 </div>
                //                 <div className="icons">
                //                   {query?.stateName?.toLowerCase()?.includes("replied") ? (
                //                     <EyeOutlined
                //                       onClick={() => handleViewQueryModal(query)}
                //                     />
                //                   ) : query?.stateName?.toLowerCase()?.includes("received") ? (
                //                     auth?.role?.role === "FO_STAFF" ? (
                //                       <Button
                //                         icon={<MessageOutlined />}
                //                         style={{
                //                           background: "#00549E",
                //                           color: "white",
                //                           borderRadius: "16px",
                //                         }}
                //                         onClick={() => handleReplyQueryModal(query)}
                //                       >
                //                         Reply
                //                       </Button>
                //                     ) : (
                //                       <EyeOutlined
                //                         onClick={() => handleReplyQueryModal(query)}
                //                       />
                //                     )
                //                   ) : null}
                //                 </div>
                //               </div>
                //             </div>
                //           ))
                //         ) : (
                //           <Text type="secondary">No queries found.</Text>
                //         )}
                //       </div>
                //     )}
                //   </Card>
                <Card
                  key={stageId}
                  style={{ margin: "6px", borderRadius: 8 }}
                  bodyStyle={{
                    padding: 12,
                    background: "rgb(255 249 247)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: "4px 2px",
                      borderRadius: 8,
                      background: colorCoding?.bgHexCode || "rgb(255 249 247)",
                      // textDecoration: isExpanded ? 'underline solid white' : "none",
                    }}
                    onClick={() => toggleStage(stageId)}
                    // onClick={() => setQueryStageId(stageId)}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Text
                        strong
                        style={{
                          color: colorCoding?.textHexCode || "black",
                          // marginLeft: "10px",
                        }}
                      >
                        {patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
                          "OP" &&
                        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
                          "ECHS" &&
                        stage?.visitStage?.toUpperCase() === "PREAUTH"
                          ? stage?.alternateName
                          : stageName}
                      </Text>

                      {/* <Tag
                        color="rgb(0, 84, 157)"
                        style={{
                          fontSize: 11,
                          height: 18,
                          lineHeight: "16px",
                          color: "white",
                        }}
                      >
                        {stageWisePendingQueryCount?.[stageId] || 0}
                      </Tag> */}
                      {stageWisePendingQueryCount?.[stageId] > 0 && (
                        <div
                          style={{
                            position: "relative",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Badge
                            count={stageWisePendingQueryCount?.[stageId] || 0}
                            overflowCount={99}
                            size="small"
                            offset={[4, -2]} // controls badge position
                            style={{
                              backgroundColor: "#e02424",
                              fontSize: 10,
                            }}
                          >
                            <Tag
                              color="rgb(0, 84, 157)"
                              style={{
                                fontSize: 11,
                                height: 18,
                                lineHeight: "16px",
                                color: "white",
                                margin: 0,
                              }}
                            >
                              New
                            </Tag>
                          </Badge>
                        </div>
                      )}
                    </div>
                    <div>
                      {isEligible && (
                        <Button
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateQuery(stageId);
                            setExpandedStageId(null);
                          }}
                          style={{ marginRight: 8 }}
                        />
                      )}
                      <Button
                        size="small"
                        icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                      />
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ marginTop: 12 }}>
                      {loadingStages ? ( //loadingStages[stageId]
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Spin />
                        </div>
                      ) : // queriesData?.length ? (
                      //   queriesData?.map((query: any) => (
                      stageQueries[stageId]?.length ? (
                        stageQueries[stageId]?.map((query: any) => (
                          <div
                            key={query?.id}
                            style={{
                              borderBottom: "0.4px solid #A6A6A6",
                              padding: "10px 0",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <Title
                                  level={5}
                                  style={{ margin: 0, fontSize: "14px" }}
                                >
                                  {query?.receivedQuery}
                                </Title>
                                {query?.stateName === "Query" && (
                                  // ?.toLowerCase()
                                  // ?.includes("received")
                                  <Tag
                                    color="red"
                                    style={{
                                      fontSize: 10,
                                      height: 18,
                                      lineHeight: "16px",
                                    }}
                                  >
                                    new
                                  </Tag>
                                )}
                              </div>

                              <Tag
                                color="#FED7AA"
                                style={{
                                  fontSize: 11,
                                  height: 18,
                                  lineHeight: "16px",
                                  color: "black",
                                }}
                              >
                                {query?.typeOfQuery || "Internal"}
                              </Tag>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <div>
                                <Text
                                  type="secondary"
                                  style={{
                                    display: "block",
                                    marginTop: 4,
                                    fontSize: "11px",
                                  }}
                                >
                                  {query?.updatedAt
                                    ? dayjs(query?.updatedAt)
                                        .tz("Asia/Kolkata")
                                        .format("DD MMM YYYY, hh:mm A")
                                    : null}
                                </Text>
                                <Tag
                                  color={
                                    query?.reQuery &&
                                    query?.stateName?.toLowerCase() === "query"
                                      ? "red"
                                      : query?.stateName
                                            ?.toLowerCase()
                                            ?.includes("replied") ||
                                          query?.stateName
                                            ?.toLowerCase()
                                            ?.includes("submitted")
                                        ? "#008B8B33"
                                        : query?.stateName === "Query"
                                          ? // ?.toLowerCase()
                                            // ?.includes("received")
                                            "#f0ad4e"
                                          : "none"
                                  }
                                  style={{
                                    marginTop: 12,
                                    fontSize: 12,
                                    borderRadius: 8,
                                    color:
                                      query?.reQuery &&
                                      query?.stateName?.toLowerCase() ===
                                        "query"
                                        ? "red"
                                        : query?.stateName
                                              ?.toLowerCase()
                                              ?.includes("replied") ||
                                            query?.stateName
                                              ?.toLowerCase()
                                              ?.includes("submitted")
                                          ? "#008B8B"
                                          : "white",
                                  }}
                                >
                                  {/* {query?.stateName}
                                   */}
                                  {query?.reQuery &&
                                  query?.stateName?.toLowerCase() ===
                                    "query" ? (
                                    // <Tag color="red" style={{ marginLeft: 8 }}>
                                    "Re-Query"
                                  ) : (
                                    // </Tag>
                                    <span>{query?.stateName ?? "-"}</span>
                                  )}
                                </Tag>
                              </div>
                              <div className="icons">
                                {query?.stateName
                                  ?.toLowerCase()
                                  ?.includes("replied") ||
                                query?.stateName
                                  ?.toLowerCase()
                                  ?.includes("submitted") ? (
                                  <EyeOutlined
                                    onClick={() => {
                                      // setQueryStageId(stageId);
                                      handleViewQueryModal(query);
                                    }}
                                  />
                                ) : query?.stateName === "Query" ? (
                                  // ?.toLowerCase()
                                  // ?.includes("received")
                                  auth?.role?.role === "FO_STAFF" ? (
                                    <Button
                                      icon={<MessageOutlined />}
                                      style={{
                                        background: "#00549E",
                                        color: "white",
                                        borderRadius: "16px",
                                      }}
                                      onClick={() => {
                                        // setQueryStageId(stageId);
                                        handleReplyQueryModal(query);
                                      }}
                                    >
                                      Reply
                                    </Button>
                                  ) : (
                                    <EyeOutlined
                                      onClick={() => {
                                        // setQueryStageId(stageId);
                                        handleReplyQueryModal(query);
                                      }}
                                    />
                                  )
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <Text type="secondary">No queries found.</Text>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
        </div>
      )}
    </Card>
  );
};

export default PatientDetailsQueryCardAllStages;
